import React, { useState, useEffect } from "react";
import { Create, useForm, useSelect } from "@refinedev/antd";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  Card,
  Row,
  Col,
  Table,
  InputNumber,
  Typography,
  message,
  Divider,
  Alert,
  Avatar,
  Upload,
  List as AntList,
  Popconfirm,
  Switch,
  Radio,
  Checkbox,
} from "antd";
import { 
  PlusOutlined, 
  DeleteOutlined, 
  UserOutlined, 
  HomeOutlined, 
  BankOutlined, 
  UploadOutlined, 
  InboxOutlined, 
  FilePdfOutlined 
} from "@ant-design/icons";
import dayjs from "dayjs";
import { IInvoice, IInvoiceItem, ISender, IGuest, IInvoiceAttachment, IRecurringSettings } from "../../interfaces";
import { SearchSelectButton } from "../../components/SearchSelectButton";
import { AdditionalProductsSelect } from "../../components/AdditionalProductsSelect";
import { RecurringInvoiceSettings } from "../../components/RecurringInvoiceSettings";
import { DebugApiTest } from "../../components/DebugApiTest";
import { splitAddress, combineAddress, testAddressSplitting } from "../../utils/addressUtils";
import { formatPrice } from "../../utils/priceUtils";


const { TextArea } = Input;
const { Title, Text } = Typography;

// Helper function to safely convert business_account value
const safeBusinessAccount = (value: any): number => {
  if (typeof value === 'object' || value === null || value === undefined) {
    console.warn('⚠️ business_account ist ein Objekt und wird zu 0 konvertiert:', value);
    return 0;
  }
  const parsed = parseInt(String(value));
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to sanitize guest data to prevent object rendering errors
const sanitizeGuestData = (guestData: any) => {
  if (!guestData || typeof guestData !== 'object') {
    return guestData;
  }

  const sanitized = { ...guestData };
  
  // Sichere business_account speziell ab
  if ('business_account' in sanitized) {
    sanitized.business_account = safeBusinessAccount(sanitized.business_account);
  }

  // Alle anderen Felder auf primitive Werte prüfen
  Object.keys(sanitized).forEach(key => {
    if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      console.warn(`⚠️ Objekt in Gast-Daten gefunden bei ${key}:`, sanitized[key]);
      // Versuche JSON.stringify für Debugging, aber setze als String
      sanitized[key] = JSON.stringify(sanitized[key]);
    }
  });

  return sanitized;
};

export const InvoiceCreate: React.FC = () => {
  const { formProps, saveButtonProps } = useForm<IInvoice>({
    onMutationSuccess: () => {
      message.success("Rechnung erfolgreich erstellt!");
    },
    onMutationError: (error) => {
      console.error("🚨 Error creating invoice:", error);
      console.error("🚨 Error details:", JSON.stringify(error, null, 2));
      message.error(`Fehler beim Erstellen der Rechnung: ${error?.message || 'Unbekannter Fehler'}`);
    }
  });

  // State für Formular-Daten
  const [items, setItems] = useState<IInvoiceItem[]>([]);
  const [selectedSender, setSelectedSender] = useState<ISender | null>(null);
  const [selectedGuest, setSelectedGuest] = useState<IGuest | null>(null);
  const [invoiceType, setInvoiceType] = useState<"invoice" | "storno" | "noshow" | "offer">("invoice");
  const [recurringSettings, setRecurringSettings] = useState<IRecurringSettings | undefined>();
  const [showRecurringSettings, setShowRecurringSettings] = useState(false);
  const [offerConfirmed, setOfferConfirmed] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [attachments, setAttachments] = useState<IInvoiceAttachment[]>([]);
  const [businessAccount, setBusinessAccount] = useState(false);
  const [childrenCount, setChildrenCount] = useState(0);
  const [childrenAges, setChildrenAges] = useState<string[]>([]);
  const [updatedRawJson, setUpdatedRawJson] = useState<any>(null);

  // Funktion zur Generierung eines dynamischen Gastnamens
  const generateGuestDisplayName = (guest: any): string => {
    if (!guest) return '';
    
    // Priorität 1: Vollständiger Name wenn vorhanden
    if (guest.name && guest.name.trim() !== '') {
      return guest.name;
    }
    
    // Priorität 2: Nachname, Vorname
    const parts: string[] = [];
    if (guest.lastname) {
      parts.push(guest.lastname);
    }
    if (guest.firstname) {
      parts.push(guest.firstname);
    }
    
    if (parts.length > 0) {
      return parts.join(', ');
    }
    
    // Priorität 3: E-Mail-Adresse
    if (guest.email) {
      return guest.email;
    }
    
    // Priorität 4: Firma
    if (guest.company) {
      return guest.company;
    }
    
    // Fallback
    return 'Unbekannter Gast';
  };

  // Funktion zur Generierung einer temporären Rechnungsnummer
  const generateTemporaryInvoiceNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const timestamp = now.getTime().toString().slice(-4);
    return `TEMP-${year}${month}${day}-${timestamp}`;
  };

  // URL Parameter processing for booking prefill
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const bookingId = urlParams.get('booking');
    const prefillData = urlParams.get('prefill');
    const typeParam = urlParams.get('type');
    
    if (bookingId && prefillData) {
      try {
        const decodedPrefill = JSON.parse(decodeURIComponent(prefillData));
        console.log('🔄 Processing booking prefill data:', decodedPrefill);
        
        // Set form values from prefill data
        if (decodedPrefill) {
                     // Set guest data
           if (decodedPrefill.guest_name || decodedPrefill.guest_email) {
             const guestData = {
               id: parseInt(decodedPrefill.guest_id) || 0,
               name: decodedPrefill.guest_name || '',
               email: decodedPrefill.guest_email || '',
               phone: decodedPrefill.guest_phone || '',
               company: String(decodedPrefill.guest_company || '0')
             };
             setSelectedGuest(guestData as unknown as IGuest);
           }
          
                     // Set booking timing
           formProps.form?.setFieldsValue({
             checkin: decodedPrefill.check_in ? dayjs(decodedPrefill.check_in) : undefined,
             checkout: decodedPrefill.check_out ? dayjs(decodedPrefill.check_out) : undefined,
             adults: parseInt(decodedPrefill.adults) || 1,
             children_count: parseInt(decodedPrefill.children_count) || 0,
             dogs: parseInt(decodedPrefill.dogs) || 0,
             booking_id: parseInt(decodedPrefill.booking_id) || undefined,
             advert_id: parseInt(decodedPrefill.advert_id) || undefined
           });
          
          // Set children data
          if (decodedPrefill.children && Array.isArray(decodedPrefill.children)) {
            setChildrenCount(decodedPrefill.children.length);
            setChildrenAges(decodedPrefill.children.map((child: any) => child.age || ''));
          }
          
          // Process calculation data and items
          if (decodedPrefill.calculation && decodedPrefill.calculation.offer) {
            const offer = decodedPrefill.calculation.offer;
            const calculatedItems: IInvoiceItem[] = [];
            
            // Process offer items (Normal, etc.)
            Object.entries(offer).forEach(([key, value]: [string, any]) => {
              if (value && typeof value === 'object' && value.price) {
                calculatedItems.push({
                  id: Date.now() + Math.random(), // Unique numeric ID
                  description: `${decodedPrefill.advert_title || 'Unterkunft'} - ${key}`,
                  price: parseFloat(value.price) || 0,
                  quantity: parseInt(value.amountDays) || 1,
                  total: parseFloat(value.price) || 0,
                  tax: 19 // Default VAT rate
                });
              }
            });
            
            setItems(calculatedItems);
            
            // Store raw calculation data
            setUpdatedRawJson({
              booking_id: decodedPrefill.booking_id,
              advert_id: decodedPrefill.advert_id,
              calculation: decodedPrefill.calculation,
              positions: {
                prisePeriod: Object.entries(offer).map(([key, value]: [string, any]) => ({
                  name: key,
                  amountDays: value.amountDays || 1,
                  pricePerDay: parseFloat(value.price) / (value.amountDays || 1),
                  totalPrice: parseFloat(value.price) || 0,
                  info: value.info || {}
                }))
              }
            });
          }
          
          // Load invoice number sequence from advert if available
          if (decodedPrefill.advert_id) {
            loadInvoiceNumberSequence(decodedPrefill.advert_id);
          }
          
          message.success(`Buchungsdaten für "${decodedPrefill.booking_title}" wurden geladen`);
        }
      } catch (error) {
        console.error('❌ Error parsing prefill data:', error);
        message.error('Fehler beim Verarbeiten der Buchungsdaten');
      }
    }
    
    // Set invoice type from URL parameter
    if (typeParam && (typeParam === 'offer' || typeParam === 'invoice' || typeParam === 'storno' || typeParam === 'noshow')) {
      setInvoiceType(typeParam);
      console.log('🔄 Invoice type set from URL:', typeParam);
    }
  }, [formProps.form]);

  // Load invoice number sequence from advert
  const loadInvoiceNumberSequence = async (advertId: number) => {
    try {
      const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=advert&tx_buchungsmanager_frontend[id]=${advertId}`;
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      const data = await response.json();
      
      if (data && data.invoiceNumberSequence) {
        // Set invoice number sequence in form
        formProps.form?.setFieldsValue({
          invoice_number_sequence: data.invoiceNumberSequence
        });
        console.log('📄 Invoice number sequence loaded:', data.invoiceNumberSequence);
      }
    } catch (error) {
      console.error('❌ Error loading invoice number sequence:', error);
    }
  };

  // Test der Adress-Aufspaltung (kann entfernt werden nach dem Test)
  useEffect(() => {
    console.log("🧪 Testing Address Splitting:");
    testAddressSplitting();
  }, []);

  // Funktion zum Berechnen der Nächte basierend auf Check-in und Check-out
  const calculateNights = (checkin: string, checkout: string): number => {
    console.log("🌙 Calculating nights:", { checkin, checkout });
    if (!checkin || !checkout) {
      console.log("🌙 Missing dates, returning 0");
      return 0;
    }
    const checkinDate = dayjs(checkin);
    const checkoutDate = dayjs(checkout);
    const nights = checkoutDate.diff(checkinDate, 'day');
    console.log("🌙 Calculated nights:", nights);
    return nights;
  };

  // Funktion zum Aktualisieren der Rechnungspositionen basierend auf Nächten
  const updateItemsForNights = (nights: number) => {
    if (nights <= 0) return;
    
    // Finde Positionen die nach Nächten berechnet werden (z.B. Übernachtungspreis)
    const updatedItems = items.map(item => {
      // Prüfe ob der Beschreibung nach es sich um eine nachtbasierte Position handelt
      const isNightBased = item.description.toLowerCase().includes('nacht') || 
                          item.description.toLowerCase().includes('übernachtung') ||
                          item.description.toLowerCase().includes('accommodation') ||
                          item.description.toLowerCase().includes('unterkunft');
      
      if (isNightBased) {
        return {
          ...item,
          quantity: nights,
          total: item.price * nights
        };
      }
      return item;
    });
    
    setItems(updatedItems);
    
    // Aktualisiere auch die prisePeriod in raw_json wenn vorhanden
    if (updatedRawJson?.positions?.prisePeriod) {
      const updatedPrisePeriod = updatedRawJson.positions.prisePeriod.map((period: any) => ({
        ...period,
        amountDays: nights,
        // Berechne den Gesamtpreis neu basierend auf den neuen Tagen
        totalPrice: period.pricePerDay ? period.pricePerDay * nights : period.totalPrice
      }));
      
      // Aktualisiere die raw_json Struktur
      const newUpdatedRawJson = {
        ...updatedRawJson,
        positions: {
          ...updatedRawJson.positions,
          prisePeriod: updatedPrisePeriod
        }
      };
      
      // Setze die aktualisierte raw_json zurück (wird beim nächsten Speichern verwendet)
      setUpdatedRawJson(newUpdatedRawJson);
      console.log('Updated prisePeriod with new nights:', newUpdatedRawJson);
    }
  };

  // API URL für Debug-Zwecke
  const getApiUrl = (): string => {
    const currentUrl = window.location.href;
    const baseUrl = currentUrl.split('/pages/')[0];
    return `${baseUrl}/api`;
  };

  // Handler für Sender-Auswahl
  const handleSenderSelect = (value: any, option: any) => {
    console.log('handleSenderSelect - value:', value, 'option:', option);
    
    if (option && option.item) {
      setSelectedSender(option.item);
      
      // Aktualisiere Accommodation-Daten basierend auf dem Sender
      if (option.item.raw_json) {
        // Verwende raw_json Daten falls vorhanden
        const accommodationData = {
          name: option.item.title || option.item.name,
          advert_id: option.item.uid,
          checkin: option.item.raw_json.booking_timing?.startDate ? 
            dayjs.unix(option.item.raw_json.booking_timing.startDate) : undefined,
          checkout: option.item.raw_json.booking_timing?.endDate ? 
            dayjs.unix(option.item.raw_json.booking_timing.endDate) : undefined,
          nights: option.item.raw_json.booking_timing?.overnights || 0,
          guests: option.item.raw_json.booking_personal?.adults || 2,
          adults: option.item.raw_json.booking_personal?.adults || 2,
          children_count: option.item.raw_json.booking_personal?.children?.count || 0,
          children_ages: option.item.raw_json.booking_personal?.children?.age || [],
          dogs: option.item.raw_json.booking_personal?.dogs || 0
        };
        
        formProps.form?.setFieldsValue({
          accommodation: accommodationData
        });
        
        // Setze childrenAges State
        if (accommodationData.children_ages && accommodationData.children_ages.length > 0) {
          setChildrenCount(accommodationData.children_count);
          setChildrenAges(accommodationData.children_ages);
        }
        
        // Berechne Nächte falls checkin/checkout vorhanden
        if (accommodationData.checkin && accommodationData.checkout) {
          const nights = calculateNights(
            accommodationData.checkin.format('YYYY-MM-DD'),
            accommodationData.checkout.format('YYYY-MM-DD')
          );
          formProps.form?.setFieldsValue({
            accommodation: {
              ...accommodationData,
              nights: nights
            }
          });
          updateItemsForNights(nights);
        }
      } else {
        // Fallback auf direkte Properties
        const accommodationData = {
          name: option.item.title || option.item.name,
          advert_id: option.item.uid,
          checkin: undefined,
          checkout: undefined,
          nights: 0,
          guests: 2,
          adults: 2,
          children_count: 0,
          children_ages: [],
          dogs: 0
        };
        
        formProps.form?.setFieldsValue({
          accommodation: accommodationData
        });
      }
    }
  };

  // Handler für Gast-Auswahl
  const handleGuestSelect = (value: any, option: any) => {
    console.log('handleGuestSelect - value:', value, 'option:', option);
    
    if (option && option.item) {
      // Sanitize guest data first
      const sanitizedGuestData = sanitizeGuestData(option.item);
      setSelectedGuest(sanitizedGuestData);
      
      // Setze businessAccount basierend auf dem Gast
      const businessAccountValue = safeBusinessAccount(sanitizedGuestData.business_account);
      setBusinessAccount(businessAccountValue === 1);
      
      // Adress-Aufspaltung für persönliche Adresse
      const personalAddressSplit = sanitizedGuestData.street && sanitizedGuestData.housnr
        ? { street: sanitizedGuestData.street, houseNumber: sanitizedGuestData.housnr }
        : splitAddress(sanitizedGuestData.address || '');

      // Adress-Aufspaltung für Firmenadresse
      const companyAddressSplit = sanitizedGuestData.company_street && sanitizedGuestData.company_housnr
        ? { street: sanitizedGuestData.company_street, houseNumber: sanitizedGuestData.company_housnr }
        : splitAddress(sanitizedGuestData.company_address || '');

      formProps.form?.setFieldsValue({
        guest: {
          id: sanitizedGuestData.uid,
          name: sanitizedGuestData.name,
          firstname: sanitizedGuestData.firstname,
          lastname: sanitizedGuestData.lastname,
          email: sanitizedGuestData.email,
          phone: sanitizedGuestData.mobile || sanitizedGuestData.phone || '',
          street: personalAddressSplit.street,
          house_number: personalAddressSplit.houseNumber,
          zip: sanitizedGuestData.zip,
          city: sanitizedGuestData.city,
          country: sanitizedGuestData.country,
          company: sanitizedGuestData.company || '',
          company_tax_id: sanitizedGuestData.company_tax_id || '',
          company_street: companyAddressSplit.street,
          company_house_number: companyAddressSplit.houseNumber,
          company_zip: sanitizedGuestData.company_zip,
          company_city: sanitizedGuestData.company_city,
          company_country: sanitizedGuestData.company_country,
          business_account: businessAccountValue
        },
      });
    }
  };

  // Handler für Rechnungstyp-Änderung
  const handleInvoiceTypeChange = (value: "invoice" | "storno" | "noshow" | "offer") => {
    setInvoiceType(value);
    
    // Bei Storno-Rechnungen alle Beträge negieren
    if (value === "storno") {
      const updatedItems = items.map(item => ({
        ...item,
        quantity: -Math.abs(item.quantity),
        total: -Math.abs(item.total),
      }));
      setItems(updatedItems);
    }
  };

  // Funktion zum Hinzufügen einer neuen Position
  const addItem = () => {
    const newItem: IInvoiceItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      price: 0,
      tax: 19,
      total: 0
    };
    setItems([...items, newItem]);
  };

  // Funktion zum Hinzufügen einer Position aus Zusatzprodukten
  const addItemFromProduct = (newItem: IInvoiceItem) => {
    setItems([...items, { ...newItem, id: Date.now() }]);
  };

  // Funktion zum Entfernen einer Position
  const removeItem = (id: number) => {
    setItems(items.filter(item => item.id !== id));
  };

  // Funktion zum Aktualisieren einer Position
  const updateItem = (id: number, field: keyof IInvoiceItem, value: any) => {
    const updatedItems = items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        // Berechne Gesamtbetrag neu
        if (field === 'quantity' || field === 'price') {
          updatedItem.total = updatedItem.quantity * updatedItem.price;
        }
        
        return updatedItem;
      }
      return item;
    });
    
    setItems(updatedItems);
  };

  // Funktion zum Berechnen der Gesamtbeträge
  const calculateTotals = () => {
    const validItems = items.filter(item => item.description && item.quantity && item.price);
    
    const subtotal = validItems.reduce((sum, item) => sum + item.total, 0);
    
    const tax7 = validItems
      .filter((item) => item.tax === 7)
      .reduce((sum, item) => sum + item.total, 0) * 0.07;
    
    const tax19 = validItems
      .filter((item) => item.tax === 19)
      .reduce((sum, item) => sum + item.total, 0) * 0.19;
    
    const total = subtotal + tax7 + tax19;
    
    return { subtotal, tax7, tax19, total };
  };

  const totals = calculateTotals();

  // Transform form values before sending to API
  const transformFormValues = (values: any) => {
    // Generiere Rechnungsnummer wenn keine angegeben
    if (!values.number || values.number.trim() === '') {
      values.number = generateTemporaryInvoiceNumber();
      console.log('📋 Automatisch generierte Rechnungsnummer:', values.number);
    }

    // Anpassung für Storno-Rechnungen (Beträge negieren)
    let adjustedItems = [...items];
    if (invoiceType === "storno") {
      adjustedItems = items.map(item => ({
        ...item,
        quantity: -Math.abs(item.quantity),
        total: -Math.abs(item.total),
      }));
    }

    // Füge type und vat zu den Items hinzu (für API-Kompatibilität)
    adjustedItems = adjustedItems.map(item => ({
      ...item,
      type: 'additional_price',
      vat: item.tax // vat ist das gleiche wie tax
    }));

    // Kombiniere Straße und Hausnummer für Backwards Compatibility
    const guestAddress = combineAddress(values.guest?.street || '', values.guest?.house_number || '');
    const companyAddress = combineAddress(values.guest?.company_street || '', values.guest?.company_house_number || '');

    // Helper function to safely format dates
    const formatDate = (dateValue: any): string | undefined => {
      console.log("🗓️ Formatting date:", dateValue, typeof dateValue);
      if (!dateValue) return undefined;
      // If it's already a string, return it
      if (typeof dateValue === 'string') {
        console.log("🗓️ Date is already string:", dateValue);
        return dateValue;
      }
      // If it's a dayjs object, format it
      if (dateValue && typeof dateValue.format === 'function') {
        const formatted = dateValue.format("YYYY-MM-DD");
        console.log("🗓️ Formatted dayjs date:", formatted);
        return formatted;
      }
      console.log("🗓️ Could not format date, returning undefined");
      return undefined;
    };

    // Füge die berechneten Werte hinzu
    const invoiceData: Partial<IInvoice> = {
      ...values,
      type: invoiceType,
      // Add confirmed status for offers
      ...(invoiceType === 'offer' && { confirmed: offerConfirmed }),
      // Explizite Verknüpfungen für Backend
      booking_id: parseInt(values.booking_id) || undefined,
      advert_id: parseInt(values.advert_id) || undefined,
      customer_id: parseInt(values.guest?.id) || undefined,
      items: adjustedItems,
      totals: invoiceType === "storno" ? {
        subtotal: -Math.abs(totals.subtotal),
        tax7: -Math.abs(totals.tax7),
        tax19: -Math.abs(totals.tax19),
        total: -Math.abs(totals.total),
      } : totals,
      date: formatDate(values.date),
      // Erweiterte Gastdaten mit Backwards Compatibility
      guest: {
        ...values.guest,
        address: guestAddress,
        company_address: companyAddress,
        business_account: safeBusinessAccount(businessAccount ? 1 : 0),
        // Entferne leere Firmenfelder wenn kein Geschäftskonto
        ...(businessAccount ? {} : {
          company: '',
          company_tax_id: ''
        })
      },
      accommodation: {
        ...values.accommodation,
        checkin: formatDate(values.accommodation?.checkin),
        checkout: formatDate(values.accommodation?.checkout),
      },
      attachments,
      // Wichtig: Raw JSON Strukturen für korrekte Speicherung (gleiches Format wie edit.tsx)
      raw_json: {
        calculation: {
          totalWithVat: invoiceType === "storno" ? -Math.abs(totals.total) : totals.total,
          totalWithOutVat: invoiceType === "storno" ? -Math.abs(totals.subtotal) : totals.subtotal,
          vat: {
            ...(totals.tax7 > 0 && { 7: invoiceType === "storno" ? -Math.abs(totals.tax7) : totals.tax7 }),
            ...(totals.tax19 > 0 && { 19: invoiceType === "storno" ? -Math.abs(totals.tax19) : totals.tax19 })
          }
        },
        positions: {
          additionalPrice: adjustedItems.map(item => ({
            description: item.description,
            count: item.quantity,
            price: item.price,
            totalPrice: item.total,
            vat: item.tax,
            isSelected: true
          }))
        },
        additional_positions: {
          sender: {
            id: values.sender?.id
          }
        },
        taxes: [],
        // booking_personal: Guest-Daten im Format wie edit.tsx
        booking_personal: {
          business_account: safeBusinessAccount(businessAccount ? 1 : 0),
          name: values.guest?.name || '',
          firstname: values.guest?.firstname || '',
          lastname: values.guest?.lastname || '',
          email: values.guest?.email || '',
          phone: values.guest?.phone || '',
          street: values.guest?.street || '',
          house_number: values.guest?.house_number || '',
          zip: values.guest?.zip || '',
          city: values.guest?.city || '',
          country: values.guest?.country || 'Deutschland',
          address: guestAddress,
          company_address: companyAddress,
          company: values.guest?.company || '',
          company_tax_id: values.guest?.company_tax_id || ''
        },
        // booking_timing: Unterkunftsdaten im Format wie edit.tsx
        booking_timing: {
          name: values.accommodation?.name || '',
          checkin: formatDate(values.accommodation?.checkin),
          checkout: formatDate(values.accommodation?.checkout),
          nights: parseInt(values.accommodation?.nights) || 0,
          guests: parseInt(values.accommodation?.guests) || null,
          advert_id: parseInt(values.accommodation?.advert_id) || null,
          adults: parseInt(values.accommodation?.adults) || 2,
          children_count: parseInt(values.accommodation?.children_count) || 0,
          dogs: parseInt(values.accommodation?.dogs) || 0
        }
      }
    };

    console.log("Transformed Invoice Data for API:", invoiceData);
    return invoiceData;
  };

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form 
        {...formProps} 
        layout="vertical" 
        onFinish={(values) => {
          console.log("🔄 Form submitted with values:", values);
          const transformedValues = transformFormValues(values);
          console.log("🔄 Transformed values for API:", transformedValues);
          // Wichtig: Rufe formProps.onFinish mit den transformierten Werten auf
          return formProps.onFinish?.(transformedValues);
        }}
      >
        <Row gutter={16}>
          {/* Basis-Informationen */}
          <Col span={24}>
            <Card title="Basis-Informationen" size="small">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Absender"
                    name="sender"
                    rules={[{ required: true, message: "Bitte Absender auswählen" }]}
                  >
                    <SearchSelectButton<ISender>
                      resource="senders"
                      title="Absender auswählen"
                      placeholder="Klicken um Absender zu suchen"
                      searchFields={["name", "company", "email"]}
                      renderItem={(item) => ({
                        title: item.company || item.name || '',
                        description: `${item.address || ''} ${item.city || ''}`.trim(),
                        meta: item.email || '',
                        avatar: <Avatar icon={<BankOutlined />} style={{ backgroundColor: '#1890ff' }} />
                      })}
                      value={formProps.form?.getFieldValue(["sender", "id"])}
                      displayValue={selectedSender?.company || selectedSender?.name || ''}
                      onChange={(value, item) => {
                        if (item) {
                          handleSenderSelect(value, { item });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Rechnungsnummer"
                    name="number"
                    rules={[]}
                    tooltip="Leer lassen für automatische Vergabe"
                  >
                    <Input placeholder="Optional - wird automatisch vergeben" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Rechnungsdatum"
                    name="date"
                    rules={[{ required: true, message: "Bitte Datum auswählen" }]}
                    initialValue={dayjs()}
                    getValueFromEvent={(date) => date?.format('YYYY-MM-DD')}
                    getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD.MM.YYYY"
                      defaultValue={dayjs()}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Rechnungstyp"
                    name="type"
                    initialValue="invoice"
                  >
                    <Select
                      placeholder="Rechnungstyp auswählen"
                      value={invoiceType}
                      onChange={handleInvoiceTypeChange}
                    >
                      <Select.Option value="invoice">Standard-Rechnung</Select.Option>
                      <Select.Option value="offer">Angebot</Select.Option>
                      <Select.Option value="storno">Storno-Rechnung</Select.Option>
                      <Select.Option value="noshow">No-Show-Rechnung</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                {invoiceType === 'offer' && (
                  <Col span={8}>
                    <Form.Item style={{ marginTop: '32px' }}>
                                             <Checkbox
                         checked={offerConfirmed}
                         onChange={(e: any) => setOfferConfirmed(e.target.checked)}
                       >
                        Als bestätigt markieren
                      </Checkbox>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Card>
          </Col>

          {/* Gast-Auswahl */}
          <Col span={24}>
            <Card title="Gast-Auswahl" size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Gast"
                    name={["guest", "id"]}
                    tooltip="Optional - Sie können einen bestehenden Gast auswählen oder die Daten manuell eingeben"
                  >
                    <SearchSelectButton<IGuest>
                      resource="guests"
                      title="Gast auswählen"
                      placeholder="Klicken um Gast zu suchen"
                      searchFields={["name", "firstname", "lastname", "email", "phone"]}
                      renderItem={(item) => {
                        const displayName = generateGuestDisplayName(item);
                        const addressParts = [];
                        if (item.address || (item.street && item.housnr)) {
                          addressParts.push(item.address || `${item.street} ${item.housnr}`);
                        }
                        if (item.city) {
                          addressParts.push(item.city);
                        }
                        const description = addressParts.join(', ');
                        
                        return {
                          title: displayName,
                          description: description || item.company || '',
                          meta: item.email || '',
                          avatar: <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#52c41a' }} />
                        };
                      }}
                      value={formProps.form?.getFieldValue(["guest", "id"])}
                      displayValue={generateGuestDisplayName(selectedGuest)}
                      onChange={(value, item) => {
                        if (item) {
                          handleGuestSelect(value, { item });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Geschäftskonto"
                    name={["guest", "business_account"]}
                    initialValue={0}
                    getValueFromEvent={(e) => {
                      // Sichere Konvertierung zu Zahl
                      return typeof e?.target?.value === 'number' ? e.target.value : 0;
                    }}
                    normalize={(value) => {
                      // Sichere Normalisierung - verhindert Objekte
                      return safeBusinessAccount(value);
                    }}
                  >
                    <Radio.Group 
                      value={safeBusinessAccount(businessAccount ? 1 : 0)}
                      onChange={(e) => {
                        const safeValue = safeBusinessAccount(e.target.value);
                        setBusinessAccount(safeValue === 1);
                        formProps.form?.setFieldsValue({
                          guest: {
                            ...formProps.form?.getFieldValue('guest'),
                            business_account: safeValue
                          }
                        });
                      }}
                    >
                      <Radio value={0}>Privatkonto</Radio>
                      <Radio value={1}>Geschäftskonto</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              {/* Gast-Details Formular */}
              <Divider orientation="left">Gast - Persönliche Daten</Divider>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item 
                    label="Vorname" 
                    name={["guest", "firstname"]}
                    rules={[{ required: true, message: "Bitte Vorname eingeben" }]}
                  >
                    <Input placeholder="Vorname" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    label="Nachname" 
                    name={["guest", "lastname"]}
                    rules={[{ required: true, message: "Bitte Nachname eingeben" }]}
                  >
                    <Input placeholder="Nachname" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    label="E-Mail" 
                    name={["guest", "email"]}
                    rules={[
                      { required: true, message: "Bitte E-Mail eingeben" },
                      { type: 'email', message: "Bitte gültige E-Mail eingeben" }
                    ]}
                  >
                    <Input placeholder="E-Mail" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={10}>
                  <Form.Item 
                    label="Straße" 
                    name={["guest", "street"]}
                    rules={[{ required: true, message: "Bitte Straße eingeben" }]}
                  >
                    <Input placeholder="Straße" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item 
                    label="Hausnummer" 
                    name={["guest", "house_number"]}
                    rules={[{ required: true, message: "Bitte Hausnummer eingeben" }]}
                  >
                    <Input placeholder="Hausnummer" />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item label="Telefon" name={["guest", "phone"]}>
                    <Input placeholder="Telefonnummer" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item 
                    label="PLZ" 
                    name={["guest", "zip"]}
                    rules={[{ required: true, message: "Bitte PLZ eingeben" }]}
                  >
                    <Input placeholder="PLZ" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label="Ort" 
                    name={["guest", "city"]}
                    rules={[{ required: true, message: "Bitte Ort eingeben" }]}
                  >
                    <Input placeholder="Ort" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Land" name={["guest", "country"]}>
                    <Input placeholder="Land" />
                  </Form.Item>
                </Col>
              </Row>

              {/* Gast - Firmendaten (nur bei Geschäftskonto) */}
              {businessAccount && (
                <>
                  <Divider orientation="left">Gast - Firmendaten</Divider>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item 
                        label="Firmenname" 
                        name={["guest", "company"]}
                        rules={businessAccount ? [{ required: true, message: "Firmenname ist erforderlich" }] : []}
                      >
                        <Input placeholder="Firmenname" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        label="USt-IdNr." 
                        name={["guest", "company_tax_id"]}
                        tooltip="Umsatzsteuer-Identifikationsnummer"
                      >
                        <Input placeholder="USt-IdNr." />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </Card>
          </Col>

          {/* Unterkunft & Aufenthalt */}
          <Col span={24}>
            <Card title="Unterkunft & Aufenthalt" size="small">
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item 
                    label="Check-in" 
                    name={["accommodation", "checkin"]}
                    getValueFromEvent={(date) => date?.format('YYYY-MM-DD')}
                    getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD.MM.YYYY"
                      onChange={(date) => {
                        console.log("🏠 Check-in date changed:", date);
                        if (date) {
                          const checkin = date.format('YYYY-MM-DD');
                          const checkout = formProps.form?.getFieldValue(['accommodation', 'checkout']);
                          console.log("🏠 Check-in processing:", { checkin, checkout });
                          if (checkout) {
                            const nights = calculateNights(checkin, checkout);
                            formProps.form?.setFieldsValue({
                              accommodation: {
                                ...formProps.form?.getFieldValue('accommodation'),
                                nights: nights
                              }
                            });
                            updateItemsForNights(nights);
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item 
                    label="Check-out" 
                    name={["accommodation", "checkout"]}
                    getValueFromEvent={(date) => date?.format('YYYY-MM-DD')}
                    getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD.MM.YYYY"
                      onChange={(date) => {
                        console.log("🏠 Check-out date changed:", date);
                        if (date) {
                          const checkout = date.format('YYYY-MM-DD');
                          const checkin = formProps.form?.getFieldValue(['accommodation', 'checkin']);
                          console.log("🏠 Check-out processing:", { checkin, checkout });
                          if (checkin) {
                            const nights = calculateNights(checkin, checkout);
                            formProps.form?.setFieldsValue({
                              accommodation: {
                                ...formProps.form?.getFieldValue('accommodation'),
                                nights: nights
                              }
                            });
                            updateItemsForNights(nights);
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Anzahl Nächte" name={["accommodation", "nights"]}>
                    <InputNumber min={1} placeholder="Nächte" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Anzahl Gäste" name={["accommodation", "guests"]}>
                    <InputNumber min={1} placeholder="Gäste" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Advert ID" name={["accommodation", "advert_id"]}>
                    <InputNumber 
                      placeholder="Advert ID (optional)" 
                      style={{ width: '100%' }} 
                      readOnly
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">Personenanzahl</Divider>
              
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item 
                    label="Erwachsene" 
                    name={["accommodation", "adults"]}
                    initialValue={2}
                  >
                    <InputNumber 
                      min={1} 
                      placeholder="Anzahl" 
                      style={{ width: '100%' }} 
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item 
                    label="Kinder" 
                    name={["accommodation", "children_count"]}
                    initialValue={0}
                  >
                    <InputNumber 
                      min={0} 
                      placeholder="Anzahl" 
                      style={{ width: '100%' }}
                      onChange={(value) => {
                        const count = value || 0;
                        setChildrenCount(count);
                        // Passe das childrenAges Array an
                        const newAges = [...childrenAges];
                        if (count > childrenAges.length) {
                          // Füge leere Strings für neue Kinder hinzu
                          for (let i = childrenAges.length; i < count; i++) {
                            newAges.push('');
                          }
                        } else {
                          // Entferne überschüssige Einträge
                          newAges.splice(count);
                        }
                        setChildrenAges(newAges);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item 
                    label="Haustiere" 
                    name={["accommodation", "dogs"]}
                    initialValue={0}
                    tooltip="Anzahl der Hunde/Haustiere"
                  >
                    <InputNumber 
                      min={0} 
                      placeholder="Anzahl" 
                      style={{ width: '100%' }} 
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Dynamische Felder für Kinder-Alter */}
              {childrenCount > 0 && (
                <>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item label="Alter/Jahrgang der Kinder">
                        <Space wrap>
                          {Array.from({ length: childrenCount }, (_, index) => (
                            <Form.Item
                              key={index}
                              name={["accommodation", "children_ages", index]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input 
                                placeholder={`Kind ${index + 1} Alter/Jahr`} 
                                style={{ width: 150 }}
                                value={childrenAges[index]}
                                onChange={(e) => {
                                  const newAges = [...childrenAges];
                                  newAges[index] = e.target.value;
                                  setChildrenAges(newAges);
                                }}
                              />
                            </Form.Item>
                          ))}
                        </Space>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </Card>
          </Col>

          {/* Rechnungspositionen */}
          <Col span={24}>
            <Card 
              title={
                <Space>
                  <span>Rechnungspositionen</span>
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />} 
                    onClick={addItem}
                    size="small"
                  >
                    Position hinzufügen
                  </Button>
                  <AdditionalProductsSelect 
                    senderId={formProps.form?.getFieldValue(["sender", "id"])}
                    onAddToInvoice={addItemFromProduct}
                  />
                </Space>
              } 
              size="small"
            >
              <Table
                dataSource={items}
                columns={[
                  {
                    title: "Beschreibung",
                    dataIndex: "description",
                    key: "description",
                    width: 300,
                    render: (_: any, record: IInvoiceItem) => (
                      <Input
                        value={record.description}
                        onChange={(e) => updateItem(record.id, "description", e.target.value)}
                        placeholder="Beschreibung eingeben"
                      />
                    ),
                  },
                  {
                    title: "Menge",
                    dataIndex: "quantity",
                    key: "quantity",
                    width: 100,
                    render: (_: any, record: IInvoiceItem) => (
                      <InputNumber
                        min={0}
                        step={1}
                        value={record.quantity}
                        onChange={(value) => updateItem(record.id, "quantity", value || 0)}
                        style={{ width: "100%" }}
                      />
                    ),
                  },
                  {
                    title: "Preis",
                    dataIndex: "price",
                    key: "price",
                    width: 150,
                    render: (_: any, record: IInvoiceItem) => (
                      <InputNumber
                        min={0}
                        step={0.01}
                        value={record.price}
                        onChange={(value) => updateItem(record.id, "price", value || 0)}
                        formatter={(value) => `€ ${value}`}
                      />
                    ),
                  },
                  {
                    title: "MwSt %",
                    dataIndex: "tax",
                    key: "tax",
                    width: 100,
                    render: (_: any, record: IInvoiceItem) => (
                      <Select
                        value={record.tax ?? 19}
                        onChange={(value) => {
                          updateItem(record.id, "tax", value);
                        }}

                        style={{ width: "100%" }}
                      >
                        <Select.Option value={0}>0%</Select.Option>
                        <Select.Option value={7}>7%</Select.Option>
                        <Select.Option value={19}>19%</Select.Option>
                      </Select>
                    ),
                  },
                  {
                    title: "Gesamt",
                    dataIndex: "total",
                    key: "total",
                    width: 150,
                    render: (_: any, record: IInvoiceItem) => (
                      <Typography.Text strong>
                        {formatPrice(record?.total || 0)}
                      </Typography.Text>
                    ),
                  },
                  {
                    title: "",
                    key: "action",
                    width: 50,
                    render: (_: any, record: IInvoiceItem) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeItem(record.id)}
                      />
                    ),
                  },
                ]}
                pagination={false}
                size="small"
                scroll={{ x: 800 }}
              />

              {/* Zusammenfassung der Beträge */}
              {items.length > 0 && (
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                    <Space direction="vertical" size="small">
                      <div>Zwischensumme: <Text strong>{formatPrice(totals.subtotal)}</Text></div>
                      {totals.tax7 > 0 && (
                        <div>MwSt 7%: <Text strong>{formatPrice(totals.tax7)}</Text></div>
                      )}
                      {totals.tax19 > 0 && (
                        <div>MwSt 19%: <Text strong>{formatPrice(totals.tax19)}</Text></div>
                      )}
                      <Divider style={{ margin: '8px 0' }} />
                      <div style={{ fontSize: '16px' }}>
                        Gesamtbetrag: <Text strong style={{ fontSize: '18px' }}>{formatPrice(totals.total)}</Text>
                      </div>
                    </Space>
                  </Card>
                </div>
              )}
            </Card>
          </Col>

          {/* Rechnungstext & Notizen */}
          <Col span={24}>
            <Card title="Rechnungstext & Notizen" size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Rechnungstext" name="text">
                    <TextArea 
                      rows={4} 
                      placeholder="Text für die Rechnung (wird auf der Rechnung angezeigt)" 
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Interne Notizen" name="notes">
                    <TextArea 
                      rows={4} 
                      placeholder="Interne Notizen (nicht auf der Rechnung sichtbar)" 
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Form>
    </Create>
  );
}; 